import * as api from '@/api/emergency'

const DATA_TYPE_SYNONYMS = {
  '固废处置单位': '危废处置单位',
  '应急处置单位': '危废处置单位'
}

const normalizeType = (state, type) => {
  if (!type) return type
  if (state.dataTypes.includes(type)) {
    return type
  }
  const displayMatch = Object.entries(state.dataTypeDisplayMap).find(([, display]) => display === type)
  if (displayMatch) {
    return displayMatch[0]
  }
  return DATA_TYPE_SYNONYMS[type] || type
}

const state = {
  // 数据类型列表（后端数据类型）
  dataTypes: [
    '应急物资库',
    '物资生产企业',
    '专家支撑单位',
    '应急救援队伍',
    '危废处置单位', // 与后端数据库保持一致
    '应急空间与设施',
    '国省考断面',
    '河流基础信息',
    '饮用水源地',
    '重点环境风险源',
    '生态管控区'
  ],

  // 显示名称映射（前端显示用）
  dataTypeDisplayMap: {
    '应急物资库': '应急物资库',
    '物资生产企业': '物资生产企业',
    '专家支撑单位': '专家支撑单位',
    '应急救援队伍': '应急救援队伍',
    '危废处置单位': '固废处置单位', // 后端使用"危废处置单位"，前端显示"固废处置单位"
    '应急空间与设施': '应急空间与设施',
    '国省考断面': '国省考断面',
    '河流基础信息': '河流基础信息',
    '饮用水源地': '饮用水源地',
    '重点环境风险源': '重点环境风险源',
    '生态管控区': '生态管控区'
  },

  // 所有应急数据点
  emergencyPoints: [],

  // 河流数据（线数据）
  rivers: [],

  // 生态管控区数据（面数据）
  ecologicalZones: [],

  // 当前模式：overview(数据概览) 或 emergency(应急支持)
  currentMode: 'overview',

  // 应急模式下筛选的数据
  emergencyFilteredData: {},

  // 上次应急事件中心点
  lastEmergencyCenterPoint: null,

  // 数据统计
  dataStats: {}
}

const mutations = {
  SET_EMERGENCY_POINTS(state, points) {
    state.emergencyPoints = points || []
  },
  
  ADD_EMERGENCY_POINTS(state, points) {
    if (Array.isArray(points)) {
      state.emergencyPoints.push(...points)
    }
  },
  
  SET_CURRENT_MODE(state, mode) {
    state.currentMode = mode
  },
  
  SET_EMERGENCY_FILTERED_DATA(state, data) {
    state.emergencyFilteredData = data || {}
  },
  
  SET_LAST_EMERGENCY_CENTER_POINT(state, point) {
    state.lastEmergencyCenterPoint = point
  },
  
  SET_DATA_STATS(state, stats) {
    state.dataStats = stats || {}
  },
  
  UPDATE_DATA_TYPE_POINTS(state, { dataType, points }) {
    // 移除该类型的旧数据
    state.emergencyPoints = state.emergencyPoints.filter(item => item.类型 !== dataType)
    // 添加新数据
    if (Array.isArray(points)) {
      state.emergencyPoints.push(...points)
    }
  },
  
  CLEAR_ALL_DATA(state) {
    state.emergencyPoints = []
    state.rivers = []
    state.ecologicalZones = []
    state.emergencyFilteredData = {}
    state.lastEmergencyCenterPoint = null
    state.dataStats = {}
  },

  // 设置河流数据
  SET_RIVERS(state, rivers) {
    state.rivers = rivers || []
  },

  // 设置生态管控区数据
  SET_ECOLOGICAL_ZONES(state, zones) {
    state.ecologicalZones = zones || []
  }
}

const actions = {
  // 加载所有应急数据
  async loadEmergencyData({ commit, state }) {
    try {
      // 开始加载应急数据
      const allPoints = []
      const progressiveStats = state.dataTypes.reduce((acc, type) => {
        acc[type] = 0
        return acc
      }, {})

      commit('SET_EMERGENCY_POINTS', [])
      commit('SET_RIVERS', [])
      commit('SET_ECOLOGICAL_ZONES', [])
      commit('SET_DATA_STATS', { ...progressiveStats })

      for (const dataType of state.dataTypes) {
        try {
          const data = await api.getData(dataType)
          // 数据加载结果正常
          
          if (Array.isArray(data)) {
            // 为每个数据点添加类型标识
            const pointsWithType = data.map(item => ({
              ...item,
              name: item.name || item.unit_name || item.river_name || item.control_unit_name || item.名称 || '',
              类型: dataType
            }))
            allPoints.push(...pointsWithType)

            commit('ADD_EMERGENCY_POINTS', pointsWithType)

            if (dataType === '河流基础信息') {
              commit('SET_RIVERS', pointsWithType)
            }

            if (dataType === '生态管控区') {
              commit('SET_ECOLOGICAL_ZONES', pointsWithType)
            }

            progressiveStats[dataType] = pointsWithType.length
            commit('SET_DATA_STATS', { ...progressiveStats })
          } else {
            console.warn(`${dataType} 返回的不是数组:`, data);
          }
        } catch (error) {
          // 401 认证错误静默处理，避免暴露 API 端点
          if (!error._isAuthError) {
            console.warn(`加载数据失败 (${dataType})`)
          }
        }
      }
      
      // 数据加载汇总完成
      
      return allPoints
    } catch (error) {
      // 401 认证错误静默处理
      if (!error._isAuthError) {
        console.warn('加载应急数据失败')
      }
      throw error
    }
  },
  
  // 按类型加载应急数据
  async loadEmergencyDataByType({ commit, state }, payload) {
    const requestedType = typeof payload === 'string' ? payload : payload?.dataType
    try {
      const normalizedType = normalizeType(state, requestedType)
      if (!normalizedType) {
        return []
      }
      const data = await api.getData(normalizedType)
      if (Array.isArray(data)) {
        const pointsWithType = data.map(item => ({
          ...item,
          name: item.name || item.unit_name || item.river_name || item.control_unit_name || item.名称 || '',
          类型: normalizedType
        }))
        commit('UPDATE_DATA_TYPE_POINTS', { dataType: normalizedType, points: pointsWithType })

        if (normalizedType === '河流基础信息') {
          commit('SET_RIVERS', pointsWithType)
        }

        if (normalizedType === '生态管控区') {
          commit('SET_ECOLOGICAL_ZONES', pointsWithType)
        }

        // 更新统计
        const stats = { ...state.dataStats }
        stats[normalizedType] = pointsWithType.length
        commit('SET_DATA_STATS', stats)
      }
      return data
    } catch (error) {
      // 401 认证错误静默处理
      if (!error._isAuthError) {
        console.warn(`加载数据失败 (${requestedType})`)
      }
      throw error
    }
  },
  
  // 清除所有数据
  async clearAllData({ commit, state }) {
    try {
      // 调用后端清除接口
      for (const dataType of state.dataTypes) {
        try {
          await api.deleteDataByType(normalizeType(state, dataType))
        } catch (error) {
          if (process.env.NODE_ENV === 'development' && !error._isAuthError) {
            console.warn(`清除数据失败 (${dataType})`)
          }
        }
      }

      // 清除本地数据
      commit('CLEAR_ALL_DATA')

    } catch (error) {
      if (process.env.NODE_ENV === 'development' && !error._isAuthError) {
        console.warn('清除所有数据失败')
      }
      throw error
    }
  },
  
  // 切换模式
  switchMode({ commit }, mode) {
    commit('SET_CURRENT_MODE', mode)
  },
  
  // 设置应急筛选数据
  setEmergencyFilteredData({ commit }, data) {
    commit('SET_EMERGENCY_FILTERED_DATA', data)
  },
  
  async updateDataTypePoints({ commit, state }, { dataType, points }) {
    const normalizedType = normalizeType(state, dataType)
    const normalizedPoints = Array.isArray(points)
      ? points.map(item => ({
          ...item,
          name: item.name || item.unit_name || item.river_name || item.control_unit_name || item.名称 || '',
          类型: normalizedType
        }))
      : []

    commit('UPDATE_DATA_TYPE_POINTS', { dataType: normalizedType, points: normalizedPoints })

    if (normalizedType === '河流基础信息') {
      commit('SET_RIVERS', normalizedPoints)
    }

    if (normalizedType === '生态管控区') {
      commit('SET_ECOLOGICAL_ZONES', normalizedPoints)
    }

    const stats = { ...state.dataStats }
    stats[normalizedType] = normalizedPoints.length
    commit('SET_DATA_STATS', stats)

    return normalizedPoints
  },

  // 更新数据统计（用于强制触发响应式更新）
  updateDataStats({ commit }, stats) {
    commit('SET_DATA_STATS', stats)
  },

  async loadDataStats({ commit, state }) {
    try {
      const response = await api.getStatistics()

      const rawData = response?.data || response || {}

      // 检查是否有嵌套的data字段
      const payload = (rawData.data && typeof rawData.data === 'object')
        ? rawData.data
        : rawData

      const stats = {}

      Object.entries(payload).forEach(([key, value]) => {
        if (key === 'code' || key === 'message') {
          return
        }
        const normalizedKey = normalizeType(state, key)
        stats[normalizedKey] = Number(value) || 0
      })

      state.dataTypes.forEach(type => {
        if (stats[type] === undefined) {
          stats[type] = 0
        }
      })

      commit('SET_DATA_STATS', stats)
      return stats
    } catch (error) {
      if (!error._isAuthError) {
        console.warn('加载统计数据失败')
      }
      throw error
    }
  },

  // 加载河流数据
  async loadRivers({ commit, state }) {
    try {
      const response = await api.getData('河流基础信息')
      const rivers = Array.isArray(response)
        ? response.map(item => ({
            ...item,
            name: item.name || item.river_name || item.河流名称 || item.名称 || '',
            类型: '河流基础信息'
          }))
        : []

      // 提交到store
      commit('SET_RIVERS', rivers)
      commit('SET_DATA_STATS', {
        ...state.dataStats,
        河流基础信息: rivers.length
      })

      return rivers
    } catch (error) {
      if (!error._isAuthError) {
        console.warn('加载河流数据失败')
      }
      commit('SET_RIVERS', [])
      return []
    }
  },

  // 加载生态管控区数据
  async loadEcologicalZones({ commit, state }) {
    try {
      const response = await api.getData('生态管控区')
      const zones = Array.isArray(response)
        ? response.map(item => ({
            ...item,
            name: item.name || item.control_unit_name || item.管控单元名称 || item.名称 || '',
            类型: '生态管控区'
          }))
        : []

      // 提交到store
      commit('SET_ECOLOGICAL_ZONES', zones)
      commit('SET_DATA_STATS', {
        ...state.dataStats,
        生态管控区: zones.length
      })

      return zones
    } catch (error) {
      if (!error._isAuthError) {
        console.warn('加载生态管控区数据失败')
      }
      commit('SET_ECOLOGICAL_ZONES', [])
      return []
    }
  }
}

const getters = {
  // 获取显示名称
  getDisplayName: (state) => (dataType) => {
    return state.dataTypeDisplayMap[dataType] || dataType
  },
  
  // 根据显示名称获取后端数据类型
  getDataTypeByDisplayName: (state) => (displayName) => {
    const entry = Object.entries(state.dataTypeDisplayMap).find(([key, value]) => value === displayName)
    return entry ? entry[0] : displayName
  },
  
  // 获取指定类型的数据点
  getPointsByType: (state) => (type) => {
    return state.emergencyPoints.filter(item => item.类型 === type)
  },
  
  // 获取数据统计
  getDataCount: (state) => (type) => {
    if (state.currentMode === 'emergency') {
      return state.emergencyFilteredData[type] ? state.emergencyFilteredData[type].length : 0
    }
    return state.emergencyPoints.filter(item => item.类型 === type).length
  },
  
  // 获取总数据量
  getTotalCount: (state) => {
    if (state.currentMode === 'emergency') {
      return Object.values(state.emergencyFilteredData).reduce((total, items) => {
        return total + (Array.isArray(items) ? items.length : 0)
      }, 0)
    }
    return state.emergencyPoints.length
  },
  
  // 获取当前模式下的所有数据
  getCurrentModeData: (state) => {
    if (state.currentMode === 'emergency') {
      return state.emergencyFilteredData
    }
    return state.emergencyPoints
  }
}

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters
}
